import React, { useEffect, useState, useMemo } from 'react';
import { File, TrendingUp, Clock, CheckCircle, Receipt } from 'lucide-react';
import AddBankModal from './components/AddBankModal';
import DeleteBankModal from './components/DeleteBankModal';
import PasswordModal from './components/PasswordModal';
import AmountModal from './components/AmountModal';
import InvoiceModal from './components/InvoiceModal';
import { useTranslation } from 'react-i18next';
import { CommissionAPI } from '@/api/type';
import { getCommissionsList, getCommissionsStats } from '@/api/commission';
import dayjs from 'dayjs';
import { addUserBankAccount, getUserBankList, deleteUserBankAccount } from '@/api/Bank';
import { Pagination } from 'antd';
import { formatCurrency } from '@/lib/commonConvert';

const CORRECT_PASSWORD = '12345678';

// const paymentHistoryData: any[] = [];

interface BankAccount {
  id: number;
  bankName: string;
  accountNumber: string;
  accountHolder: string;
  isDefault: boolean;
}

const Billing: React.FC = () => {
  // const [balance] = useState(0);
  const [withdrawAmount, setWithdrawAmount] = useState<number | null>(null);
  const [bankAccounts, setBankAccounts] = useState<BankAccount | null>(null);

  const [showAddBankModal, setShowAddBankModal] = useState(false);
  const [showDeleteBankModal, setShowDeleteBankModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showAmountModal, setShowAmountModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState<CommissionAPI | null>(null);

  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [amount, setAmount] = useState('');

  // Commission data 
  const [commissionData, setCommissionData] = useState<CommissionAPI[]>([]);
  const [commissionStats, setCommissionStats] = useState<{
    total_recorded: number,
    total_pending: number,
    total_earned: number,
    total_tax_fee: number
  }>({
    total_recorded: 0,
    total_pending: 0,
    total_earned: 0,
    total_tax_fee: 0
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCommissions, setTotalCommissions] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const { t } = useTranslation();

  const handleAddBank = (otp: string, bankData: { bankName: string; accountNumber: string; accountHolder: string; branch: string; setAsDefault: boolean }) => {
    addUserBankAccount(otp, bankData.bankName, bankData.accountNumber, bankData.accountHolder, bankData.branch, bankData.setAsDefault).then(() => {
      getUserBankList().then((data) => {
        setBankAccounts({
          id: data.data[0].id,
          bankName: data.data[0].bank_name,
          accountNumber: data.data[0].account_number,
          accountHolder: data.data[0].account_holder_name,
          isDefault: true
        });
      });
    });
    setShowAddBankModal(false);
  };

  const handleConfirmDeleteBank = (otp: string) => {
    if (!bankAccounts) return;
    deleteUserBankAccount(bankAccounts.id, otp).then((data) => {
      if (data.status) {
        setBankAccounts(null);
        setShowDeleteBankModal(false);
      }
    });
  };


  const handlePasswordSubmit = () => {
    if (password === CORRECT_PASSWORD) {
      setShowPasswordModal(false);
      setShowAmountModal(true);
      setPassword('');
      setPasswordError('');
    } else {
      setPasswordError('Mật khẩu không chính xác');
    }
  };

  const handleAmountSubmit = () => {
    const numAmount = parseFloat(amount.replace(/[^0-9]/g, ''));
    if (numAmount > 0) {
      setWithdrawAmount(numAmount);
      setShowAmountModal(false);
      setAmount('');
    }
  };


  // Fetch bank accounts 
  useEffect(() => {
    getUserBankList().then((data) => {
      if (data.data.length === 0) return;
      setBankAccounts({
        id: data.data[0].id,
        bankName: data.data[0].bank_name,
        accountNumber: data.data[0].account_number,
        accountHolder: data.data[0].account_holder_name,
        isDefault: true
      });
    });
    getCommissionsStats().then((data) => {
      if (data.status && data.data) {
        setCommissionStats(data.data);
      }
    });
  }, []);

  // Fetch commission data
  useEffect(() => {
    getCommissionsList(undefined, undefined, (dayjs().month()+1).toString(), (dayjs().year()).toString(), 'pending,waiting_tax_check', page, pageSize).then((data) => {
      setCommissionData(data.data.data.data);
      setTotalCommissions(data.data.total_commission);
      setTotalRecords(data.data.data.total);
    });
  }, [page, pageSize]);

  // Calculate dashboard stats
  const dashboardStats = useMemo(() => {
    const totalRecorded = commissionData.reduce((sum, item) => sum + Number(item.gross_amount), 0);
    const pendingApproval = commissionData
      .filter(item => ['pending', 'waiting_tax_check', 'approved'].includes(item.status))
      .reduce((sum, item) => sum + Number(item.gross_amount), 0);
    const totalPaid = commissionData
      .filter(item => item.status === 'paid')
      .reduce((sum, item) => sum + Number(item.net_amount), 0);
    const taxDeducted = commissionData.reduce((sum, item) => sum + Number(item.tax_fee), 0);

    return { totalRecorded, pendingApproval, totalPaid, taxDeducted };
  }, [commissionData]);

  return (
    <div>
      {/* Tổng Quan */}
      {/* <div className="!bg-white dark:!bg-gray-800 rounded-md p-6 mb-6">
        <h2 className="!text-xl !font-bold !text-gray-900 dark:!text-white mb-2">{t('payment.billing.overview')}</h2>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 border border-gray-200 dark:border-gray-700 p-4 rounded-sm">
          <div className="lg:col-span-2 lg:border-r border-gray-200 dark:border-gray-700 pr-4">
            <p className="!text-sm !text-gray-800 dark:!text-gray-200 mb-2">
              {t('payment.commission.total_earned')}
            </p>
            <div className="mb-2">
              <span className="!text-3xl !font-bold !text-gray-900 dark:!text-white">
                {formatCurrency(totalCommissions)}đ
              </span>
            </div>
          </div>

          <div className="lg:col-span-1 lg:border-r border-gray-200 dark:border-gray-700 pr-4">
            <p className="!text-sm font-semibold !text-gray-800 dark:!text-gray-200 mb-1">{t('payment.billing.total_paid')}</p>
            <p className="!text-xs !text-gray-800 dark:!text-gray-200 mb-3"></p>
            <p className="!text-xl !font-bold !text-gray-900 dark:!text-white">
              {withdrawAmount !== null ? `đ ${formatCurrency(withdrawAmount)}` : 'Chưa có yêu cầu'}
            </p>
          </div>

          <div className="lg:col-span-1">
            <div className="h-full">
              <div className="flex items-center justify-between mb-3">
                <h3 className="!text-sm !font-semibold !text-gray-900 dark:!text-white">{t('payment.billing.bank_account')}</h3>
                <button
                  className="flex items-center gap-1 !text-sm !text-blue-600 dark:!text-blue-400 hover:!text-blue-700 dark:hover:!text-blue-300 !bg-transparent !border-none"
                >
                  {bankAccounts ? (
                    <Popconfirm
                      title={t('payment.billing.delete_bank_account')}
                      description={t('payment.billing.delete_bank_account_confirm')}
                      onConfirm={() => {
                        bankRequestOTP().then(() => {
                           setShowDeleteBankModal(true);
                        });
                      }}
                      okText={t('common.yes')}
                      cancelText={t('common.no')}
                    >
                      <div
                        className="flex items-center gap-1 text-sm !text-red-600 dark:!text-red-400 hover:!text-red-700 dark:hover:!text-red-300">
                        {t('payment.billing.delete_bank_account')}
                        <Trash2 className="w-4 h-4" />
                      </div>
                    </Popconfirm>
                  ) : (
                    <div onClick={() => setShowAddBankModal(true)} className="flex items-center gap-1">
                      <Plus className="w-4 h-4" />
                      {t('payment.billing.add_bank_account')}
                    </div>
                  )}
                </button>
              </div>

              {bankAccounts ? (
                <div className="flex items-start gap-3">
                  <div className="!w-10 !h-10 !bg-blue-600 rounded flex items-center justify-center flex-shrink-0">
                    <span className="!text-white !font-bold !text-xs">{bankAccounts.bankName.substring(0, 3).toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="!text-sm !font-semibold !text-gray-900 dark:!text-white truncate">{bankAccounts.bankName}</p>
                      <span className="!text-xs !text-green-600 dark:!text-green-400 !bg-green-50 dark:!bg-green-900/30 !px-2 !py-0.5 rounded-sm whitespace-nowrap">{t('payment.billing.default')}</span>
                    </div>
                    <p className="!text-sm !text-gray-600 dark:!text-gray-400">**** {bankAccounts.accountNumber.slice(-4)}</p>
                    <p className="!text-xs !text-gray-500 dark:!text-gray-500 truncate">{bankAccounts.accountHolder}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="!text-sm !text-gray-400 dark:!text-gray-500">{t('payment.billing.no_bank_account')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div> */}

      {/* Dashboard Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Total Recorded */}
        <div className="!bg-white dark:!bg-gray-800 rounded-md p-5 shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="!text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase tracking-wide mb-1">
                {t('payment.dashboard.total_recorded')}
              </p>
              <p className="!text-2xl !font-bold !text-gray-900 dark:!text-white">
                {formatCurrency(commissionStats.total_recorded, 'đ')}
              </p>
              <p className="!text-xs !text-gray-400 dark:!text-gray-500 mt-1">
                {t('payment.dashboard.total_recorded_desc')}
              </p>
            </div>
            <div className="!w-12 !h-12 !bg-blue-500 rounded-full flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        {/* Pending Approval */}
        <div className="!bg-white dark:!bg-gray-800 rounded-md p-5 shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="!text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase tracking-wide mb-1">
                {t('payment.dashboard.pending_approval')}
              </p>
              <p className="!text-2xl !font-bold !text-gray-900 dark:!text-white">
                {formatCurrency(commissionStats.total_pending, 'đ')}
              </p>
              <p className="!text-xs !text-gray-400 dark:!text-gray-500 mt-1">
                {t('payment.dashboard.pending_approval_desc')}
              </p>
            </div>
            <div className="!w-12 !h-12 !bg-yellow-500 rounded-full flex items-center justify-center">
              <Clock className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        {/* Total Paid */}
        <div className="!bg-white dark:!bg-gray-800 rounded-md p-5 shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="!text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase tracking-wide mb-1">
                {t('payment.dashboard.total_paid')}
              </p>
              <p className="!text-2xl !font-bold !text-gray-900 dark:!text-white">
                {formatCurrency(commissionStats.total_earned, 'đ')}
              </p>
              <p className="!text-xs !text-gray-400 dark:!text-gray-500 mt-1">
                {t('payment.dashboard.total_paid_desc')}
              </p>
            </div>
            <div className="!w-12 !h-12 !bg-green-500 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        {/* Tax Deducted */}
        <div className="!bg-white dark:!bg-gray-800 rounded-md p-5 shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="!text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase tracking-wide mb-1">
                {t('payment.dashboard.tax_deducted')}
              </p>
              <p className="!text-2xl !font-bold !text-gray-900 dark:!text-white">
                {formatCurrency(commissionStats.total_tax_fee, 'đ')}
              </p>
              <p className="!text-xs !text-gray-400 dark:!text-gray-500 mt-1">
                {t('payment.dashboard.tax_deducted_desc')}
              </p>
            </div>
            <div className="!w-12 !h-12 !bg-red-500 rounded-full flex items-center justify-center">
              <Receipt className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      <div className="!bg-white dark:!bg-gray-800 rounded-md">
        <div className="px-6 py-4 !border-b !border-gray-200 dark:!border-gray-700">
          <h2 className="!text-lg !font-semibold !text-gray-900 dark:!text-white">{t('payment.billing.payment_history')}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="!border-b !border-gray-200 dark:!border-gray-700">
                <th className="px-6 py-3 !text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase tracking-wider text-center">{t('payment.billing.date')}</th>
                <th className="px-6 py-3 !text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase tracking-wider text-center">{t('payment.billing.from')}</th>
                <th className="px-6 py-3 !text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase tracking-wider text-center">{t('payment.billing.package')}</th>
                <th className="px-6 py-3 !text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase tracking-wider text-center">{t('payment.billing.percentage')}</th>
                <th className="px-6 py-3 !text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase tracking-wider text-center">{t('payment.billing.before_tax')}</th>
                <th className="px-6 py-3 !text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase tracking-wider text-center">{t('payment.billing.tax')}</th>
                <th className="px-6 py-3 !text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase tracking-wider text-center">{t('payment.billing.after_tax')}</th>
                <th className="px-6 py-3 !text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase tracking-wider text-center">{t('payment.billing.status')}</th>
                <th className="px-6 py-3 !text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase tracking-wider text-center">{t('payment.billing.invoice')}</th>
              </tr>
            </thead>
            <tbody className="!divide-y !divide-gray-200 dark:!divide-gray-700">
              {commissionData.map((payment) => (
                <tr key={payment.id} className="hover:!bg-gray-50 dark:hover:!bg-gray-700/30 !transition-colors">
                  {/* thêm td tương ứng với th */}
                  <td className="px-6 py-4 whitespace-nowrap text-center !text-sm !text-gray-700 dark:!text-gray-300">{dayjs(payment.payable_at).format('DD/MM/YYYY')}</td>
                  <td className="px-6 py-4 min-w-[240px] text-left !text-sm !text-gray-900 dark:!text-white !font-medium">{payment.description}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-center !text-sm !text-gray-700 dark:!text-gray-300">{payment.package_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-center !text-sm !text-gray-700 dark:!text-gray-300">{payment.commission_rate_percent}%</td>
                  <td className="px-6 py-4 whitespace-nowrap text-center !text-sm !text-gray-700 dark:!text-gray-300">{formatCurrency(Number(payment.gross_amount), 'đ')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-center !text-sm !text-gray-700 dark:!text-gray-300">{formatCurrency(Number(payment.tax_fee), 'đ')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-center !text-sm !text-gray-700 dark:!text-gray-300">{formatCurrency(Number(payment.net_amount), 'đ')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-center !text-sm !text-gray-700 dark:!text-gray-300">
                    {payment.status === 'pending' && <span className="!px-2 !py-1 !bg-yellow-100 dark:!bg-yellow-900/30 !text-yellow-800 dark:!text-yellow-300 !rounded-sm !text-xs">{t('payment.billing.status_pending')}</span>}
                    {payment.status === 'approved' && <span className="!px-2 !py-1 !bg-blue-100 dark:!bg-blue-900/30 !text-blue-800 dark:!text-blue-300 !rounded-sm !text-xs">{t('payment.billing.status_approved')}</span>}
                    {payment.status === 'paid' && <span className="!px-2 !py-1 !bg-green-100 dark:!bg-green-900/30 !text-green-800 dark:!text-green-300 !rounded-sm !text-xs">{t('payment.billing.status_paid')}</span>}
                    {payment.status === 'rejected' && <span className="!px-2 !py-1 !bg-red-100 dark:!bg-red-900/30 !text-red-800 dark:!text-red-300 !rounded-sm !text-xs">{t('payment.billing.status_rejected')}</span>}
                    {payment.status === 'waiting_tax_check' && <span className="!px-2 !py-1 !bg-purple-100 dark:!bg-purple-900/30 !text-purple-800 dark:!text-purple-300 !rounded-sm !text-xs">{t('payment.billing.status_waiting_tax_check')}</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap !text-sm flex items-center justify-center">
                    <button
                      onClick={() => {
                        setSelectedCommission(payment);
                        setShowInvoiceModal(true);
                      }}
                      title={t('payment.billing.view_invoice')}
                      className="flex items-center gap-1 !text-blue-600 dark:!text-blue-400 hover:!text-blue-700 dark:hover:!text-blue-300 !bg-transparent !border-none cursor-pointer"
                    >
                      <File className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700">
          <Pagination
            current={page}
            total={totalRecords}
            pageSize={pageSize}
            onChange={(page, pageSize) => {
              setPage(page);
              setPageSize(pageSize);
            }}
            showSizeChanger
          />
        </div>
      </div>

      {/* Modals */}
      <AddBankModal
        isOpen={showAddBankModal}
        onClose={() => setShowAddBankModal(false)}
        onAdd={handleAddBank}
      />

      <DeleteBankModal
        isOpen={showDeleteBankModal}
        onClose={() => setShowDeleteBankModal(false)}
        onConfirm={handleConfirmDeleteBank}
      />

      <PasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onSubmit={handlePasswordSubmit}
        password={password}
        setPassword={setPassword}
        passwordError={passwordError}
        setPasswordError={setPasswordError}
      />

      <AmountModal
        isOpen={showAmountModal}
        onClose={() => setShowAmountModal(false)}
        onSubmit={handleAmountSubmit}
        amount={amount}
        setAmount={setAmount}
      />

      <InvoiceModal
        isOpen={showInvoiceModal}
        onClose={() => {
          setShowInvoiceModal(false);
          setSelectedCommission(null);
        }}
        commission={selectedCommission}
        bankInfo={bankAccounts ? {
          bankName: bankAccounts.bankName,
          accountNumber: bankAccounts.accountNumber,
          accountHolder: bankAccounts.accountHolder
        } : null}
      />
    </div>
  );
};

export default Billing;
